import { useState } from 'react';
import { usersApi } from '@/api';
import type { User } from '@/types';
import { StepLayout } from '../StepLayout';

interface DoneStepProps {
  profile: User;
  onFinished: (user: User) => void;
}

export function DoneStep({ profile, onFinished }: DoneStepProps) {
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFinish = async () => {
    setFinishing(true);
    setError(null);
    try {
      const resp = await usersApi.updateProfile({ onboardingCompleted: true });
      if (resp.success && resp.data) {
        onFinished(resp.data);
      } else {
        setError(resp.error || 'Failed to mark setup complete.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to mark setup complete.';
      setError(msg);
    } finally {
      setFinishing(false);
    }
  };

  // Compact summary so the inspector can see what stuck without flipping
  // through the wizard. Only shown for fields they actually filled in.
  const filledRows: Array<[string, string]> = [];
  if (profile.companyName) filledRows.push(['Company', profile.companyName]);
  if (profile.phone) filledRows.push(['Phone', profile.phone]);
  if (profile.licenseNumber) filledRows.push(['License', profile.licenseNumber]);
  if (profile.logoUrl) filledRows.push(['Logo', 'Uploaded']);
  if (profile.agreementTemplate) filledRows.push(['Agreement', `${profile.agreementTemplate.length.toLocaleString()} characters`]);
  if (profile.slug) filledRows.push(['Booking URL', `/book/${profile.slug}`]);

  return (
    <StepLayout
      title="You're all set"
      subtitle="Setup complete. Anything you skipped lives in Settings — you can always come back."
      onPrimary={handleFinish}
      primaryLabel="Create my first inspection"
      primaryLoading={finishing}
      error={error}
    >
      {filledRows.length > 0 ? (
        <dl className="rounded-xl border border-border bg-surface-2 divide-y divide-border">
          {filledRows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <dt className="text-body-sm text-ink-subtle">{label}</dt>
              <dd className="text-body-sm text-ink font-medium truncate max-w-[60%] text-right">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-body-sm text-ink-subtle">
          You skipped every step — no problem. You can configure your business details in Settings any time.
        </p>
      )}
    </StepLayout>
  );
}
